/**
 * Marketing home page - comprehensive landing page with all key information.
 * Public-facing landing page for unauthenticated users.
 */

import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { MarketingLayout } from './MarketingLayout';
import './HomePage.css';

/* ============================================
   Data & Content
   ============================================ */

interface Feature {
    icon: string;
    title: string;
    description: string;
    benefits: string[];
}

const features: Feature[] = [
    {
        icon: '👥',
        title: 'User Management',
        description: 'Provision users, assign licenses, and manage access across your incident response workspace.',
        benefits: ['Bulk user provisioning', 'License optimization', 'Role-based access control'],
    },
    {
        icon: '🔑',
        title: 'Temporary Access Pass',
        description: 'Passwordless onboarding via SMS. Get external responders productive in minutes, not hours.',
        benefits: ['SMS-based delivery', 'Time-limited codes', 'No password setup required'],
    },
    {
        icon: '🛡️',
        title: 'Security Policies',
        description: 'Conditional Access, session controls, and MFA configured from day one. Pre-hardened by default.',
        benefits: ['Zero-trust architecture', 'Session timeouts', 'Geo-blocking available'],
    },
    {
        icon: '🏢',
        title: 'Multi-Tenant Management',
        description: 'Manage multiple client workspaces from a single dashboard. Perfect for IR firms and MSPs.',
        benefits: ['Centralized control', 'Client isolation', 'Cross-tenant reporting'],
    },
    {
        icon: '🤝',
        title: 'B2B Guest Access',
        description: 'External counsel and partners authenticate with their own SSO. No shared credentials.',
        benefits: ['External SSO support', 'Granular permissions', 'Audit-ready access logs'],
    },
    {
        icon: '📋',
        title: 'Complete Audit Trail',
        description: 'Complete activity logging for compliance and reporting. Know who did what, when.',
        benefits: ['Immutable logs', 'Export to SIEM', 'Compliance-ready reports'],
    },
];

interface UseCase {
    icon: string;
    title: string;
    audience: string;
    scenario: string;
    outcome: string;
}

const useCases: UseCase[] = [
    {
        icon: '⚖️',
        title: 'Law Firm Breach Response',
        audience: 'Privacy & Data Security Practice',
        scenario: 'Client suffers ransomware attack. Need secure workspace for privileged communications between in-house counsel, outside counsel, and forensics team within hours.',
        outcome: 'Workspace provisioned in 15 minutes. External counsel authenticated via their firm\'s SSO. All communications protected by attorney-client privilege in isolated M365 tenant.',
    },
    {
        icon: '🔒',
        title: 'Corporate Incident Response',
        audience: 'Enterprise Security Team',
        scenario: 'Suspected insider threat requires investigation outside compromised corporate environment. Need to collaborate with HR, Legal, and external forensics without alerting suspect.',
        outcome: 'Isolated workspace with no connection to corporate tenant. TAP codes issued to external investigators. Complete audit trail for potential litigation.',
    },
    {
        icon: '🏥',
        title: 'Healthcare Data Breach',
        audience: 'HIPAA-Regulated Entity',
        scenario: 'PHI exposure requires coordinated response across legal, compliance, PR, and technical teams. 72-hour notification clock is ticking.',
        outcome: 'Pre-hardened security policies ensure HIPAA compliance. Teams collaboration enables rapid coordination. Audit logs support regulatory reporting requirements.',
    },
];

interface Stat {
    value: string;
    label: string;
    detail: string;
}

const stats: Stat[] = [
    { value: '15', label: 'minutes', detail: 'Average workspace provisioning time' },
    { value: '0', label: 'training', detail: 'Hours required. Your team knows M365' },
    { value: '100%', label: 'isolated', detail: 'Separate tenant, separate risk' },
];

interface ComparisonRow {
    feature: string;
    detail: string;
    irpilot: boolean | string;
    others: boolean | string;
}

const comparisonData: ComparisonRow[] = [
    {
        feature: 'Native M365 tools',
        detail: 'Teams, SharePoint, Outlook. The tools your team uses daily',
        irpilot: true,
        others: false
    },
    {
        feature: 'Real email capability',
        detail: 'Full Outlook with custom domain, not just internal messaging',
        irpilot: true,
        others: false
    },
    {
        feature: 'External SSO support',
        detail: 'Law firms and partners use their own identity providers',
        irpilot: true,
        others: false
    },
    {
        feature: 'Transparent pricing',
        detail: 'Published rates, no "contact sales" runaround',
        irpilot: true,
        others: false
    },
    {
        feature: 'Pre-hardened security',
        detail: 'Conditional Access, MFA, session controls from day one',
        irpilot: true,
        others: 'Partial'
    },
    {
        feature: 'Zero learning curve',
        detail: 'No proprietary UI to learn during a crisis',
        irpilot: true,
        others: false
    },
    {
        feature: 'Passwordless onboarding',
        detail: 'TAP codes via SMS. External responders productive in minutes',
        irpilot: true,
        others: false
    },
];

interface FAQ {
    question: string;
    answer: string;
}

const faqs: FAQ[] = [
    {
        question: 'How quickly can we get a workspace up and running?',
        answer: 'Most workspaces are fully provisioned within 15 minutes. Security policies are applied automatically, and users can be added immediately via TAP codes or B2B guest invitations.',
    },
    {
        question: 'Is this a separate tenant from our corporate M365?',
        answer: 'Yes, completely. IRPilot provisions isolated M365 tenants with no connection to your corporate environment. This ensures containment during incidents where your primary systems may be compromised.',
    },
    {
        question: 'How do external parties (law firms, forensics) access the workspace?',
        answer: 'Two options: B2B Guest Access lets them authenticate with their own organization\'s SSO, or you can issue Temporary Access Pass (TAP) codes via SMS for immediate passwordless access.',
    },
    {
        question: 'What security policies are applied by default?',
        answer: 'All workspaces include Conditional Access policies, required MFA, session timeouts, geo-blocking options, and complete audit logging. These are configured automatically with no manual setup required.',
    },
    {
        question: 'Can we use a custom domain for email?',
        answer: 'Yes. Each workspace gets full Outlook capability. You can use the default domain or configure a custom domain for professional client-facing communications.',
    },
];

/* ============================================
   Component
   ============================================ */

export function HomePage(): JSX.Element {
    const { isAuthenticated, isLoading } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        if (!isLoading && isAuthenticated) {
            navigate('/app/workspaces', { replace: true });
        }
    }, [isAuthenticated, isLoading, navigate]);

    return (
        <MarketingLayout>
            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-container">
                    <span className="hero-badge">
                        Powered by Microsoft 365
                    </span>
                    <h1 className="hero-headline">
                        Secure Incident Response Workspaces
                    </h1>
                    <p className="hero-subheadline">
                        When your primary systems are compromised, you need a clean room.
                        IRPilot delivers pre-hardened M365 environments for crisis collaboration, provisioned in minutes,
                        using tools your team already knows.
                    </p>
                    <p className="hero-audience">
                        For incident response teams, law firms, MSSPs, and security professionals
                    </p>
                    <div className="hero-cta-group">
                        <Link to="/login" className="hero-cta-primary">
                            Get Started
                        </Link>
                        <Link to="/platform" className="hero-cta-secondary">
                            Why Microsoft 365
                        </Link>
                    </div>
                    <div className="hero-stats">
                        {stats.map((stat) => (
                            <div key={stat.label} className="hero-stat">
                                <span className="hero-stat-value">{stat.value}</span>
                                <span className="hero-stat-label">{stat.label}</span>
                                <span className="hero-stat-detail">{stat.detail}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Problem Statement */}
            <section className="problem-section">
                <div className="problem-container">
                    <div className="problem-content">
                        <span className="problem-eyebrow">The Challenge</span>
                        <h2 className="problem-headline">
                            During a breach, you need a secure space to collaborate.
                        </h2>
                        <div className="problem-points">
                            <div className="problem-point">
                                <span className="problem-point-icon">⚠️</span>
                                <div className="problem-point-text">
                                    <strong>Compromised systems can&apos;t be trusted</strong>
                                    <span>If attackers have access, they can read your response plans</span>
                                </div>
                            </div>
                            <div className="problem-point">
                                <span className="problem-point-icon">⏰</span>
                                <div className="problem-point-text">
                                    <strong>Proprietary tools require training</strong>
                                    <span>Learning new software during a crisis costs precious hours</span>
                                </div>
                            </div>
                            <div className="problem-point">
                                <span className="problem-point-icon">🔐</span>
                                <div className="problem-point-text">
                                    <strong>External parties need secure access</strong>
                                    <span>Sharing credentials with counsel and forensics teams creates risk</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="problem-solution">
                        <div className="solution-card">
                            <span className="solution-badge">The IRPilot Approach</span>
                            <p className="solution-text">
                                Isolated M365 tenants with enterprise security baked in.
                                Your team uses Teams, SharePoint, and Outlook with no learning curve.
                                External parties authenticate with their own SSO or passwordless TAP codes.
                            </p>
                            <div className="solution-features">
                                <span className="solution-feature">✓ Completely isolated tenant</span>
                                <span className="solution-feature">✓ Pre-configured security</span>
                                <span className="solution-feature">✓ Ready in 15 minutes</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Key Differentiators */}
            <section className="differentiators-section">
                <div className="differentiators-header">
                    <h2 className="section-title">Why IRPilot?</h2>
                    <p className="section-subtitle">
                        Built for the realities of incident response, not generic collaboration
                    </p>
                </div>
                <div className="differentiators-container">
                    <div className="differentiator-card featured">
                        <div className="differentiator-icon">🎯</div>
                        <h3 className="differentiator-title">Familiar Tools</h3>
                        <p className="differentiator-description">
                            Teams, SharePoint, Outlook. No new software to learn.
                            Your team works in tools they already know, even during a 3 AM incident.
                        </p>
                        <ul className="differentiator-list">
                            <li>Full Microsoft 365 suite</li>
                            <li>Real email with Outlook</li>
                            <li>Video calls via Teams</li>
                            <li>Document collaboration in SharePoint</li>
                        </ul>
                    </div>
                    <div className="differentiator-card">
                        <div className="differentiator-icon">🔒</div>
                        <h3 className="differentiator-title">Pre-Hardened Security</h3>
                        <p className="differentiator-description">
                            Security policies applied automatically.
                            Conditional access, MFA, and session controls configured from day one.
                        </p>
                        <ul className="differentiator-list">
                            <li>Zero-trust by default</li>
                            <li>Required MFA for all users</li>
                            <li>Session timeout controls</li>
                            <li>Geo-blocking available</li>
                        </ul>
                    </div>
                    <div className="differentiator-card">
                        <div className="differentiator-icon">💰</div>
                        <h3 className="differentiator-title">Transparent Pricing</h3>
                        <p className="differentiator-description">
                            Know your costs upfront. No hidden fees,
                            no surprise invoices, no &ldquo;contact sales&rdquo; runaround.
                        </p>
                        <ul className="differentiator-list">
                            <li>Published pricing</li>
                            <li>No per-incident fees</li>
                            <li>Month-to-month available</li>
                            <li>No minimum commitment</li>
                        </ul>
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section className="features-section">
                <div className="features-container">
                    <h2 className="section-title">Everything You Need for Incident Response</h2>
                    <p className="section-subtitle">Built on Microsoft 365, configured for crisis collaboration</p>
                    <div className="features-grid">
                        {features.map((feature) => (
                            <div key={feature.title} className="feature-card">
                                <div className="feature-icon">{feature.icon}</div>
                                <h3 className="feature-title">{feature.title}</h3>
                                <p className="feature-description">{feature.description}</p>
                                <ul className="feature-benefits">
                                    {feature.benefits.map((benefit) => (
                                        <li key={benefit}>{benefit}</li>
                                    ))}
                                </ul>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section className="how-it-works-section">
                <div className="how-it-works-container">
                    <h2 className="section-title">How It Works</h2>
                    <p className="section-subtitle">From incident declaration to secure collaboration in three steps</p>
                    <div className="steps-grid">
                        <div className="step-card">
                            <div className="step-number">1</div>
                            <h3 className="step-title">Provision</h3>
                            <p className="step-description">
                                Create a secure workspace in minutes.
                                Automated tenant setup with pre-configured security policies.
                                No IT involvement required.
                            </p>
                            <div className="step-time">
                                <span className="step-time-value">~15 min</span>
                                <span className="step-time-label">typical setup time</span>
                            </div>
                        </div>
                        <div className="step-connector">→</div>
                        <div className="step-card">
                            <div className="step-number">2</div>
                            <h3 className="step-title">Collaborate</h3>
                            <p className="step-description">
                                Use Teams, SharePoint, and Outlook.
                                Invite external counsel with B2B guest access or TAP codes.
                                Full email capability included.
                            </p>
                            <div className="step-time">
                                <span className="step-time-value">Immediate</span>
                                <span className="step-time-label">once provisioned</span>
                            </div>
                        </div>
                        <div className="step-connector">→</div>
                        <div className="step-card">
                            <div className="step-number">3</div>
                            <h3 className="step-title">Resolve</h3>
                            <p className="step-description">
                                Manage the incident with familiar tools.
                                Full audit trail for compliance and reporting.
                                Archive or extend as needed.
                            </p>
                            <div className="step-time">
                                <span className="step-time-value">Complete</span>
                                <span className="step-time-label">audit trail included</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Use Cases */}
            <section className="use-cases-section">
                <div className="use-cases-container">
                    <h2 className="section-title">Real-World Scenarios</h2>
                    <p className="section-subtitle">See how organizations use IRPilot during active incidents</p>
                    <div className="use-cases-grid">
                        {useCases.map((useCase) => (
                            <div key={useCase.title} className="use-case-card">
                                <div className="use-case-header">
                                    <span className="use-case-icon">{useCase.icon}</span>
                                    <div className="use-case-meta">
                                        <h3 className="use-case-title">{useCase.title}</h3>
                                        <span className="use-case-audience">{useCase.audience}</span>
                                    </div>
                                </div>
                                <div className="use-case-body">
                                    <div className="use-case-scenario">
                                        <span className="use-case-label">Scenario</span>
                                        <p>{useCase.scenario}</p>
                                    </div>
                                    <div className="use-case-outcome">
                                        <span className="use-case-label">Outcome</span>
                                        <p>{useCase.outcome}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Comparison Section */}
            <section className="comparison-section">
                <div className="comparison-container">
                    <h2 className="section-title">IRPilot vs. The Alternatives</h2>
                    <p className="section-subtitle">
                        See how we compare to proprietary incident response platforms and DIY solutions
                    </p>
                    <div className="comparison-table-wrapper">
                        <table className="comparison-table">
                            <thead>
                                <tr>
                                    <th>Capability</th>
                                    <th className="highlight-column">IRPilot</th>
                                    <th>Proprietary Platforms</th>
                                </tr>
                            </thead>
                            <tbody>
                                {comparisonData.map((row) => (
                                    <tr key={row.feature}>
                                        <td>
                                            <strong>{row.feature}</strong>
                                            <span className="comparison-detail">{row.detail}</span>
                                        </td>
                                        <td className="check-cell irpilot">
                                            {row.irpilot === true ? '✓' : row.irpilot === false ? '✗' : row.irpilot}
                                        </td>
                                        <td className="check-cell others">
                                            {row.others === true ? '✓' : row.others === false ? '✗' : row.others}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="faq-section">
                <div className="faq-container">
                    <h2 className="section-title">Frequently Asked Questions</h2>
                    <div className="faq-grid">
                        {faqs.map((faq) => (
                            <div key={faq.question} className="faq-item">
                                <h4 className="faq-question">{faq.question}</h4>
                                <p className="faq-answer">{faq.answer}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing Preview */}
            <section className="pricing-preview-section">
                <div className="pricing-preview-container">
                    <h2 className="section-title">Simple, Transparent Pricing</h2>
                    <p className="section-subtitle">
                        No hidden fees. No surprise invoices. No &ldquo;contact sales&rdquo; gatekeeping.
                    </p>
                    <div className="pricing-highlights">
                        <div className="pricing-highlight">
                            <span className="pricing-label">Platform fee</span>
                            <span className="pricing-value">Starting at $XXX/mo</span>
                            <span className="pricing-note">Includes security policies & management</span>
                        </div>
                        <div className="pricing-highlight featured">
                            <span className="pricing-label">Per user</span>
                            <span className="pricing-value">From $XX/user/mo</span>
                            <span className="pricing-note">Full M365 license included</span>
                        </div>
                        <div className="pricing-highlight">
                            <span className="pricing-label">All plans include</span>
                            <span className="pricing-value">Everything</span>
                            <span className="pricing-note">No feature gating or tiers</span>
                        </div>
                    </div>
                    <Link to="/pricing" className="pricing-cta-link">
                        See full pricing details →
                    </Link>
                </div>
            </section>

            {/* Final CTA */}
            <section className="final-cta-section">
                <div className="final-cta-container">
                    <div className="final-cta-content">
                        <h2 className="final-cta-title">
                            Enterprise incident response without the enterprise price tag
                        </h2>
                        <p className="final-cta-description">
                            The tools your team already knows. The security your clients demand.
                            Ready when you need it because incidents don&apos;t wait for procurement.
                        </p>
                        <div className="final-cta-features">
                            <span className="final-cta-feature">✓ No long-term contracts</span>
                            <span className="final-cta-feature">✓ Provision on-demand</span>
                            <span className="final-cta-feature">✓ Cancel anytime</span>
                        </div>
                    </div>
                </div>
            </section>
        </MarketingLayout>
    );
}
